/**
 * Column model for the contacts table.
 *
 * Built-in columns map to server-sortable fields (see the `sort_on` whitelist in
 * `contacts_controller.rb`). Custom-attribute columns use a `cattr:<key>` sort key
 * which the controller resolves to a JSONB ordering (see `custom_attribute_sort`).
 *
 * The active column set is persisted per-user in UI settings under
 * `contacts_table_columns` (an ordered array of column keys).
 */

export const CUSTOM_COLUMN_PREFIX = 'cattr:';

// `type` drives cell rendering in ContactsTableRow.
export const CONTACT_TABLE_COLUMNS = [
  {
    key: 'name',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.NAME',
    sortKey: 'name',
    type: 'name',
    alwaysVisible: true,
  },
  {
    key: 'email',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.EMAIL',
    sortKey: 'email',
    type: 'email',
    defaultOn: true,
  },
  {
    key: 'phone',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.PHONE',
    sortKey: 'phone_number',
    type: 'phone',
    defaultOn: true,
  },
  {
    key: 'company',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.COMPANY',
    sortKey: 'company',
    type: 'company',
  },
  {
    key: 'city',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.CITY',
    sortKey: 'city',
    type: 'city',
  },
  {
    key: 'country',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.COUNTRY',
    sortKey: 'country',
    type: 'country',
  },
  {
    key: 'created_at',
    labelKey: 'CONTACTS_LAYOUT.TABLE.COLUMNS.CREATED_AT',
    sortKey: 'created_at',
    type: 'date',
  },
];

export const ALWAYS_VISIBLE_COLUMNS = CONTACT_TABLE_COLUMNS.filter(
  column => column.alwaysVisible
);

export const DEFAULT_COLUMN_KEYS = CONTACT_TABLE_COLUMNS.filter(
  column => column.alwaysVisible || column.defaultOn
).map(column => column.key);

/**
 * Build column descriptors from contact custom-attribute definitions.
 * `defs` come from the `attributes/getContactAttributes` getter (camelCased).
 */
export const buildCustomAttributeColumns = (defs = []) =>
  defs
    .filter(def => def?.attributeKey)
    .map(def => ({
      key: `${CUSTOM_COLUMN_PREFIX}${def.attributeKey}`,
      label: def.attributeDisplayName || def.attributeKey,
      sortKey: `${CUSTOM_COLUMN_PREFIX}${def.attributeKey}`,
      type: def.attributeDisplayType || 'text',
      attributeKey: def.attributeKey,
      isCustom: true,
    }));

/**
 * Resolve the ordered list of active column descriptors.
 * Always-visible columns come first; remaining saved keys (built-in or custom)
 * follow in saved order. Falls back to the default set when nothing is saved.
 */
export const resolveActiveColumns = (savedKeys, customDefs = []) => {
  const builtinByKey = Object.fromEntries(
    CONTACT_TABLE_COLUMNS.map(column => [column.key, column])
  );
  const customColumns = buildCustomAttributeColumns(customDefs);
  const customByKey = Object.fromEntries(
    customColumns.map(column => [column.key, column])
  );

  const keys =
    Array.isArray(savedKeys) && savedKeys.length
      ? savedKeys
      : DEFAULT_COLUMN_KEYS;

  const ordered = [...ALWAYS_VISIBLE_COLUMNS];
  const alwaysKeys = new Set(ALWAYS_VISIBLE_COLUMNS.map(column => column.key));

  keys.forEach(key => {
    if (alwaysKeys.has(key)) return;
    const column = builtinByKey[key] || customByKey[key];
    if (column && !ordered.some(existing => existing.key === column.key)) {
      ordered.push(column);
    }
  });

  return ordered;
};
